/**
 * Landingspagina's — The Alpha Men
 *
 * Vier taken:
 *  1. UTM-parameters doorgeven aan de PDP-links
 *  2. Doorklikmeting: dataLayer-event plus een custom Meta-pixel-event
 *  3. Subtiele parallax op hero en close-up
 *  4. Sticky CTA op mobiel tonen/verbergen
 *
 * Geladen als type="module", dus de DOM staat er al.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------------------------------------------------------------------------
   1. UTM-doorgifte
   Alles wat in de URL van de landingspagina staat, gaat mee naar de PDP.
   Parameters die al op de CTA-link staan winnen — die zijn bewust gezet.
   --------------------------------------------------------------------------- */
function forwardQueryParams() {
  const incoming = new URLSearchParams(window.location.search);
  if (![...incoming].length) return;

  document.querySelectorAll('[data-lp-cta]').forEach((link) => {
    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.origin);
    } catch (e) {
      return;
    }

    incoming.forEach((value, key) => {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    });

    link.setAttribute('href', url.pathname + url.search + url.hash);
  });
}

/* ---------------------------------------------------------------------------
   2. Doorklikmeting LP -> PDP
   --------------------------------------------------------------------------- */
function trackCta(link) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'lp_to_pdp',
    lp: link.dataset.lpCta || 'trimmen-zonder-gedoe',
    lp_position: link.dataset.lpPosition || 'onbekend'
  });

  /* Meta pixel. De eventnaam verschilt per landingspagina en komt uit het
     data-attribuut dat lp-tzg-cta.liquid meegeeft. Synchroon in de handler,
     dus het verzoek vertrekt voordat de browser aan de navigatie begint.
     trackCustom staat los van PageView en laat dat event ongemoeid. De guard
     vangt af dat de pixel nog niet geladen is; dan slaan we hem over in
     plaats van een fout te gooien. */
  const metaEvent = link.dataset.lpMetaEvent;
  if (metaEvent && typeof window.fbq === 'function') {
    window.fbq('trackCustom', metaEvent);
  }
}

function trackClicks() {
  document.querySelectorAll('[data-lp-cta]').forEach((link) => {
    /* 'click' dekt de gewone klik, Enter op de link, en het openen in een
       nieuw tabblad met cmd- of ctrl-klik: die vuren allemaal 'click'. */
    link.addEventListener('click', () => trackCta(link));

    /* Een middenklik opent ook een nieuw tabblad, maar vuurt geen 'click'.
       Die komt binnen als 'auxclick'. Alleen button 1 meetellen: button 2 is
       de rechtermuisknop en dat is geen navigatie. */
    link.addEventListener('auxclick', (event) => {
      if (event.button === 1) trackCta(link);
    });
  });
}

/* ---------------------------------------------------------------------------
   3. Parallax — uitsluitend via transform, en alleen als het mag
   --------------------------------------------------------------------------- */
function initParallax() {
  const hero = document.querySelector('[data-lp-parallax="hero"]');
  const blade = document.querySelector('[data-lp-parallax="blade"]');
  if (REDUCED || (!hero && !blade)) return;

  let ticking = false;

  const update = () => {
    ticking = false;

    if (hero) {
      hero.style.transform = `translate3d(0, ${(window.scrollY * 0.14).toFixed(1)}px, 0)`;
    }

    if (blade) {
      const rect = blade.getBoundingClientRect();
      // Alleen rekenen als het blok in de buurt van de viewport is.
      if (rect.bottom > -200 && rect.top < window.innerHeight + 200) {
        const offset =
          (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        blade.style.transform = `translate3d(0, ${(offset * -18).toFixed(1)}px, 0)`;
      }
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
}

/* ---------------------------------------------------------------------------
   4. Sticky CTA
   Verschijnt na ongeveer een schermhoogte scrollen en verdwijnt zodra de
   productcard in beeld komt, zodat hij de CTA in die card niet dubbelt.
   De class stuurt ook visibility aan, zie lp-tzg.css: zonder dat blijft de
   knop focusbaar terwijl hij uit beeld staat.
   --------------------------------------------------------------------------- */
function initStickyCta() {
  const sticky = document.querySelector('[data-lp-sticky]');
  if (!sticky) return;

  const card = document.querySelector('[data-lp-card]');
  let cardVisible = false;

  if (card && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        cardVisible = entries[0].isIntersecting;
        sync();
      },
      { rootMargin: '0px 0px -20% 0px' }
    ).observe(card);
  }

  let ticking = false;

  function sync() {
    ticking = false;
    const scrolledEnough = window.scrollY > window.innerHeight * 0.9;
    sticky.classList.toggle('is-visible', scrolledEnough && !cardVisible);
  }

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(sync);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  sync();
}

forwardQueryParams();
trackClicks();
initParallax();
initStickyCta();
